import React, { useEffect, useState, useCallback, useRef } from 'react';
import { Box, Typography, Snackbar, Alert, Stack, Dialog, DialogTitle, DialogContent, DialogActions, Button, Checkbox, FormControlLabel, FormControl, FormHelperText } from '@mui/material';
import { styled } from '@mui/material/styles';
import { DataGrid, GridColDef, GridRowSelectionModel } from '@mui/x-data-grid';
import { jaJP } from '@mui/x-data-grid/locales';
import { useForm } from 'react-hook-form';
import { ThemeInputText, FormCard, GrayColorButton, SecondColorButton, ColorButton } from './Controls';
import { authURL, userURL, apiURL } from './App';
import { authJson, authFetch } from './apiClient';
import { User } from './Main';

// 型定義
interface DataItem { id: string; name: string; displayName: string; lastName: string; firstName: string; email: string; isAdmin: boolean; }
interface CreateForm { name: string; password: string; check_password: string; lastName: string; firstName: string; email: string; isAdmin: boolean; }
interface EditForm { name: string; lastName: string; firstName: string; email: string; isAdmin: boolean; }

// DataGrid カスタムスタイル（再マウント防止のためコンポーネント外で定義）
const ThemeDataGrid = styled(DataGrid)(({ theme }) => ({
  backgroundColor: theme.palette.secondary.light,
  '& .MuiDataGrid-columnHeader': {
    backgroundColor: theme.palette.secondary.light,
    color: theme.palette.primary.main,
    outline: 'none',
    boxShadow: 'none',
  },
  '& .MuiDataGrid-menuIconButton, & .MuiDataGrid-menuIcon, & .MuiDataGrid-iconButtonContainer': {
    display: 'none !important',
  },
  '& .MuiDataGrid-columnHeader:focus, & .MuiDataGrid-columnHeader:focus-within': {
    outline: 'none',
    boxShadow: 'none',
  },
  '& .MuiDataGrid-filler': {
    backgroundColor: theme.palette.secondary.light,
  },
  '& .MuiDataGrid-root': {
    backgroundColor: theme.palette.secondary.light,
  },
  '& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within': {
    outline: 'none',
  },
}));

// トラッキング関数（共通）
const trackAdminAction = async (userId: number, method: string, apiPath: string, apiParams: any) => {
  const tracking_data = {
    userId: userId,                 // 操作した人のID
    action: 'admin_user',           // 閲覧ログと区別
    path: apiPath,                  // APIのパス (/auth/users/等)
    mode: method,                   // POST, PATCH, DELETE等
    payload: apiParams,             // 送信したデータ内容
    timestamp: new Date().toISOString(),
  };

  try {
    await authFetch(`${apiURL}/track`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(tracking_data)
    });
  } catch (err) {
    console.error("Admin action tracking failed:", err);
  }
};

// Propsの型定義を追加
interface AdminUsersPanelProps {
  user: User;
}

// 管理者用ユーザー管理パネル
const AdminUsersPanel: React.FC<AdminUsersPanelProps> = ({ user }) => {
  // 一覧データ
  const [rows, setRows] = useState<DataItem[]>([]);
  // フィルタ
  const { control: searchControl, watch: watchSearch, setValue: setSearchValue } = useForm<{ keyword: string }>({ defaultValues: { keyword: '' } });
  const keyword = watchSearch('keyword');
  // 追加フォーム
  const { control: addControl, handleSubmit: handleAddSubmit, reset: resetAdd, getValues: getAddValues, setValue: setAddValue } = useForm<CreateForm>({ defaultValues: { name: '', password: '', check_password: '', lastName: '', firstName: '', email: '', isAdmin: false } });
  // 編集フォーム
  const { control: editControl, handleSubmit: handleEditSubmit, reset: resetEdit, setValue: setEditValue, watch: watchEdit } = useForm<EditForm>({ defaultValues: { name: '', lastName: '', firstName: '', email: '', isAdmin: false } });
  // パスワードリセットフォーム (管理者: 新パスワード + 確認)
  const { control: pwControl, handleSubmit: handlePwSubmit, reset: resetPw, setValue: setPwValue } = useForm<{ password: string; check_password: string }>({ defaultValues: { password: '', check_password: '' } });
  const [selectedId, setSelectedId] = useState<string | null>(null);
  // 通知
  const [toast, setToast] = useState<{ open: boolean; msg: string; severity: 'success'|'error'|'info'|'warning' }>({ open: false, msg: '', severity: 'info' });
  const openToast = (msg: string, severity: typeof toast.severity = 'info') => setToast({ open: true, msg, severity });

  // ビューポート高さ調整用
  const manageCardRef = useRef<HTMLDivElement | null>(null);
  const [panelHeight, setPanelHeight] = useState<number | null>(null);
  useEffect(() => {
    const calc = () => {
      if (!manageCardRef.current) return;
      const rect = manageCardRef.current.getBoundingClientRect();
      const marginBottom = 16; // 下余白
      const h = window.innerHeight - rect.top - marginBottom;
      setPanelHeight(h > 300 ? h : 300); // 最低高さ 300
    };
    calc();
    window.addEventListener('resize', calc);
    return () => window.removeEventListener('resize', calc);
  }, []);

  // スクロール位置復元用
  const gridScrollRef = useRef<HTMLDivElement | null>(null);
  const lastScrollTopRef = useRef<number>(0);

  // 一覧取得
  const fetchRows = useCallback(async () => {
    try {
      // API 呼び出し
      const data: any[] = await authJson(`${userURL}`);
      console.log(data);
      // リスト設定
      setRows((data || []).map(d => {
        const lastName = d.last_name || '';
        const firstName = d.first_name || '';
        const email = d.email || '';
        // 管理者権限は is_superuser フラグで判定
        const isAdmin = !!(d.is_superuser === 1 || d.is_superuser === true);
        const displayName = d.display_name || [lastName, firstName].filter(Boolean).join(' ') || d.username || d.name || '';
        return { id: String(d.id), name: d.username, displayName, lastName, firstName, email, isAdmin } as DataItem;
      }));
      // 選択行が消えていない場合はそのまま (DataGrid 側に rowSelectionModel 渡しているのでOK)
      // スクロール位置復元
      requestAnimationFrame(() => {
        if (gridScrollRef.current) {
          gridScrollRef.current.scrollTop = lastScrollTopRef.current;
        }
      });
    }
    catch (e) {
      // 取得エラー
      console.error(e);
      openToast('ユーザー一覧の取得に失敗しました', 'error');
      // リストクリア
      setRows([]);
    }
  }, []);
  
  // 初回ロード
  useEffect(() => {
    // リスト取得
    void fetchRows();
  }, [fetchRows]);

  // 編集対象選択
  const onSelectionChange = (sel: GridRowSelectionModel) => {
    // 選択行があればそのID、なければnull
    const id = sel.length ? String(sel[0]) : null;
    setSelectedId(id);
    // 編集フォームに設定
    if (id) {
      const item = rows.find(p => p.id === id);
      if (item) {
        setEditValue('name', item.name);
        setEditValue('lastName', item.lastName);
        setEditValue('firstName', item.firstName);
        setEditValue('email', item.email);
        setEditValue('isAdmin', item.isAdmin);
        return;
      }
    }
    // 見つからない/未選択時クリア
    resetEdit();
  };

  // 追加
  const onAdd = async (form: CreateForm) => {
    // 入力チェック
    if (!form.name?.trim()) { openToast('ユーザー名を入力してください', 'warning'); return; }
    if (!form.password?.trim()) { openToast('パスワードを入力してください', 'warning'); return; }
    if (form.password.length < 8) { openToast('パスワードは8文字以上にしてください', 'warning'); return; }
    if (form.password === form.password.replace(/\D/g, '')) { openToast('数字以外の文字も含めてください', 'warning'); return; }
    if (form.password !== form.check_password) { openToast('パスワード(確認用)が一致しません', 'warning'); return; }
    // 追加処理
    try {
      // API 呼び出し
      // バックエンドは標準ユーザー作成API: JSON 直接送信
      const payload = {
        username: form.name.trim(),
        password: form.password.trim(),
        email: form.email?.trim() || '',
        first_name: form.firstName?.trim() || '',
        last_name: form.lastName?.trim() || '',
        is_superuser: !!form.isAdmin,
      };
      // 新規ユーザー追加
      const res = await authFetch(`${authURL}/users/`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
        actionName: 'AddUser'
      });
      if (res.ok) {
        // トラッキングログ
        payload.password = "********"; // パスワードはログに残さない
        trackAdminAction(user.id, 'POST', `${authURL}/users/`, payload);
        // 追加成功
        openToast('ユーザーを追加しました', 'success');
        // 追加フォームクリア
        resetAdd();
        // 追加後に一覧再取得
        await fetchRows();
      }
    }
    catch (e) {
      // 追加エラー
      console.error('ユーザー追加エラー', e);
      // 可能ならエラーメッセージを抽出して表示
      let msg = '追加に失敗しました';
      if (e && typeof e === 'object' && 'details' in e) {
        const details = e.details; // 文字列
        if (typeof details === 'string' && details.trim()) {
          // デバッグ用ログ
          console.log('details', details);          
          // JSON なら整形
          try {
            const parsed = JSON.parse(details);
            if (parsed && typeof parsed === 'object') {
              // 最初のキーのメッセージだけ簡易抽出
              const firstKey = Object.keys(parsed)[0];
              const val = parsed[firstKey];
              if (Array.isArray(val) && val.length) {
                msg = `${firstKey}: ${val[0]}`;
              } else if (typeof val === 'string') {
                msg = `${firstKey}: ${val}`;
              } else {
                msg = typeof parsed.detail === 'string' ? parsed.detail : msg;
              }
            }
          } catch {
            // JSON でなければそのまま一部利用
            if (details.length < 160) msg = details; // 長すぎる場合は既定メッセージ維持
          }
        }
      }
      // エラーメッセージ表示
      openToast(msg, 'error');
    }
  };

  // 更新
  const onUpdate = async (form: EditForm) => {
    // 入力チェック
    if (!selectedId) { openToast('ユーザーを選択してください', 'warning'); return; }
    if (!form.name?.trim()) { openToast('ユーザー名を入力してください', 'warning'); return; }
    // 更新処理
    try {
      // API 呼び出し 
      // バックエンドは標準ユーザー作成API: JSON 直接送信
      const payload = {
        username: form.name.trim(),
        email: form.email?.trim() || '',
        first_name: form.firstName?.trim() || '',
        last_name: form.lastName?.trim() || '',
        is_superuser: form.isAdmin ? true : false,
      };
      // 選択ユーザーを更新
      const res = await authFetch(`${authURL}/users/${selectedId}/`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
        actionName: 'EditUser'
      });
      if (res.ok) {
        // トラッキングログ
        trackAdminAction(user.id, 'PATCH', `${authURL}/users/${selectedId}/`, payload);
        // 更新成功
        openToast('更新しました', 'success');
        // 更新後に一覧再取得
        await fetchRows();
      }
    }
    catch (e) {
      // 更新エラー
      console.error('ユーザー更新エラー', e);
      openToast('更新に失敗しました', 'error');
    }
  };

  // 削除確認ダイアログ制御
  const [deleteOpen, setDeleteOpen] = useState(false);
  const onDelete = () => {
    if (!selectedId) { openToast('削除対象を選択してください', 'warning'); return; }
    setDeleteOpen(true);
  };
  // 削除ダイアログ閉じる
  const closeDeleteDialog = () => setDeleteOpen(false);

  // 削除
  const handleDeleteConfirm = async () => {
    try {
      // API 呼び出し
      // 選択ユーザーを削除
      const res = await authFetch(`${userURL}/${selectedId}/`, { method: 'DELETE', actionName: 'RemoveUser' });
      if (res.ok) {
        // トラッキングログ
        trackAdminAction(user.id, 'DELETE', `${userURL}/${selectedId}/`, { });
        // 削除成功
        openToast('削除しました', 'success');
        // 選択解除 & 編集フォームクリア
        setSelectedId(null);
        resetEdit();
        // 一覧再取得
        await fetchRows();
      }
    }
    catch (e) {
      // 削除エラー
      console.error('ユーザー削除エラー', e);
      openToast('削除に失敗しました', 'error');
    }
    // ダイアログ閉じる
    setDeleteOpen(false);
  };

  // パスワードリセットダイアログ制御
  const [pwOpen, setPwOpen] = useState(false);
  const openPwDialog = () => {
    if (!selectedId) { openToast('対象ユーザーを選択してください', 'warning'); return; }
    setPwOpen(true);
  };
  const closePwDialog = () => { setPwOpen(false); resetPw(); };
  // パスワードリセット (管理者: 現在のPW不要)
  const onResetPassword = async (form: { password: string; check_password: string }) => {
    if (!selectedId) { openToast('対象ユーザーを選択してください', 'warning'); return; }
    if (!form.password?.trim()) { openToast('新しいパスワードを入力してください', 'warning'); return; }
    if (form.password.length < 8) { openToast('パスワードは8文字以上にしてください', 'warning'); return; }
    if (form.password === form.password.replace(/\D/g, '')) { openToast('数字以外の文字も含めてください', 'warning'); return; }
    if (form.password !== form.check_password) { openToast('確認用パスワードが一致しません', 'warning'); return; }
    try {
      // API 呼び出し
      // 選択ユーザーのパスワードをリセット
      // バックエンドは専用API: ユーザーIDと新パスワードを JSON で送信
      // 例: { "new_password": "xxxx" }
      // 古い API (パスワードリセット要求) は使用しないことに注意
      // const payload = { id: selectedId, new_password: form.password.trim() };
      const payload = { new_password: form.password.trim() };
      const res = await authFetch(`${userURL}/${selectedId}/set_password/`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
        actionName: 'ResetPassword'
      });
      if (res.ok) {
        // トラッキングログ
        payload.new_password = "********"; // パスワードはログに残さない
        trackAdminAction(user.id, 'POST', `${userURL}/${selectedId}/set_password/`, payload);
        // 成功
        openToast(`パスワードをリセットしました`, 'success');
        // ダイアログ閉じる
        closePwDialog();
      }
    }
    catch (e) {
      // 追加エラー
      console.error('パスワードリセットエラー', e);
      // 可能ならエラーメッセージを抽出して表示
      let msg = 'パスワードリセットに失敗しました';
      if (e && typeof e === 'object' && 'details' in e) {
        const details = e.details; // 文字列
        if (typeof details === 'string' && details.trim()) {
          // デバッグ用ログ
          console.log('details', details);          
          // JSON なら整形
          try {
            const parsed = JSON.parse(details);
            if (parsed && typeof parsed === 'object') {
              // 最初のキーのメッセージだけ簡易抽出
              const firstKey = Object.keys(parsed)[0];
              const val = parsed[firstKey];
              if (Array.isArray(val) && val.length) {
                msg = `${firstKey}: ${val[0]}`;
              } else if (typeof val === 'string') {
                msg = `${firstKey}: ${val}`;
              } else {
                msg = typeof parsed.detail === 'string' ? parsed.detail : msg;
              }
            }
          } catch {
            // JSON でなければそのまま一部利用
            if (details.length < 160) msg = details; // 長すぎる場合は既定メッセージ維持
          }
        }
      }
      // エラーメッセージ表示
      openToast(msg, 'error');
    }
  };

  // フィルタ適用
  const filteredRows = rows.filter(p => !keyword?.trim() || p.name.toLowerCase().includes(keyword.trim().toLowerCase()));

  // グリッド列定義
  const columns: GridColDef[] = [
    { field: 'name', headerName: 'ユーザー名', flex: 1, minWidth: 140 },
    { field: 'displayName', headerName: '表示名', flex: 1, minWidth: 140 },
  ];

  // DataGrid スクロール位置保持: DataGrid 内部の仮想スクロール要素を取得
  const gridContainerCallback = (el: HTMLDivElement | null) => {
    if (el) {
      // DataGrid のスクロールコンテナ (MuiDataGrid-virtualScroller) を探す
      const scroller = el.querySelector('.MuiDataGrid-virtualScroller');
      if (scroller instanceof HTMLDivElement) {
        if (!gridScrollRef.current) {
          // 初期登録: スクロールイベントで位置保存
          scroller.addEventListener('scroll', () => {
            lastScrollTopRef.current = scroller.scrollTop;
          });
        }
        gridScrollRef.current = scroller;
      }
    }
  };

  // レンダリング
  return (
    <Box display="flex" gap={2}>
      {/* 左側: ユーザー追加 (35%) */}
      <Box sx={{ width: '35%' }}>
        <FormCard style={{ textAlign: 'left', display: 'flex', flexDirection: 'column' }}>
          <Typography variant="h5" sx={{ fontWeight: 'bold', alignSelf: 'flex-start', mb: 2 }}>ユーザー追加</Typography>
          <form onSubmit={handleAddSubmit(onAdd)} autoComplete="off">
            {/* ブラウザの資格情報自動入力抑止用ダミーフィールド (Chrome 対策) */}
            <input type="text" name="fake_disable_username" autoComplete="username" style={{ display: 'none' }} aria-hidden="true" />
            <input type="password" name="fake_disable_password" autoComplete="new-password" style={{ display: 'none' }} aria-hidden="true" />
            <Stack direction="column" spacing={2}>
              <ThemeInputText id="name" autoComplete="off" label="ユーザー名" placeholder="ユーザー名を入力" helperText="必ず指定。半角英数字と記号(@/./+/-/_ )が使用できます" control={addControl} />
              <ThemeInputText id="password" autoComplete="off" type="password" label="パスワード" placeholder="パスワードを入力" helperText="8文字以上。使い回しや辞書の語、数字だけ、個人情報は設定できません" control={addControl} />
              <ThemeInputText id="check_password" autoComplete="off" type="password" label="パスワード（確認）" placeholder="もう一度パスワードを入力" helperText="確認のため同じパスワードを再入力してください" control={addControl} />
              <Box display="flex" gap={2}>
                <Box flex={1}>
                  <ThemeInputText id="lastName" label="姓" placeholder="姓を入力" control={addControl} />
                </Box>
                <Box flex={1}>
                  <ThemeInputText id="firstName" label="名" placeholder="名を入力" control={addControl} />
                </Box>
              </Box>
              <ThemeInputText id="email" type="email" label="メールアドレス（任意）" placeholder="メールアドレスを入力" control={addControl} />
              <FormControl variant="standard" sx={{ alignItems: 'flex-start', mt: -0.5 }}>
                <FormControlLabel
                  control={<Checkbox defaultChecked={false} onChange={(_, checked) => { setAddValue('isAdmin', checked); }} />}
                  label="管理者権限を付与する"
                />
                <FormHelperText sx={{ ml: 0, mt: -1 }} id="isAdmin-helper-add">チェックすると管理者モードにアクセス可能になります</FormHelperText>
              </FormControl>
              <Box display="flex" gap={1} justifyContent="flex-end">
                <GrayColorButton onClick={() => resetAdd()}>クリア</GrayColorButton>
                <SecondColorButton type="submit">追加</SecondColorButton>
              </Box>
            </Stack>
          </form>
        </FormCard>
      </Box>
      {/* 右側: ユーザー管理 (65%) */}
      <Box sx={{ width: '65%' }}>
        <FormCard ref={manageCardRef} style={{ textAlign: 'left', display: 'flex', flexDirection: 'column', height: panelHeight ? panelHeight : 'auto' }}>
          <Typography variant="h5" sx={{ fontWeight: 'bold', alignSelf: 'flex-start', mb: 2 }}>ユーザー管理</Typography>
          <Box sx={{ mb: 2 }}>
            <ThemeInputText id="keyword" label="ユーザー検索" placeholder="ユーザー名でフィルタ" control={searchControl} />
          </Box>
          <Box display="flex" gap={2} alignItems="flex-start" sx={{ flexGrow: 1, minHeight: 0 }}>
            {/* DataGrid 45% */}
            <Box sx={{ width: '45%', height: '100%', display: 'flex', flexDirection: 'column', minHeight: 0 }}>
              <Box sx={{ flexGrow: 1, minHeight: 0 }} ref={gridContainerCallback}>
                <ThemeDataGrid
                  rows={filteredRows}
                  columns={columns}
                  checkboxSelection={false}
                  onRowSelectionModelChange={onSelectionChange}
                  rowSelectionModel={selectedId ? [selectedId] : []}
                  hideFooter
                  isCellEditable={() => false}
                  localeText={jaJP.components.MuiDataGrid.defaultProps.localeText}
                  sx={{ height: '100%' }}
                />
              </Box>
            </Box>
            {/* 編集パネル & 削除ボタン 55% */}
            <Box sx={{ width: '55%', height: '100%', display: 'flex', flexDirection: 'column' }}>
              {/* 編集パネル */}
              <FormCard style={{ width: '100%' }}>
                <Typography variant="h5" sx={{ fontWeight: 'bold', mb: 2 }}>ユーザー編集</Typography>
                <form onSubmit={handleEditSubmit(onUpdate)}>
                  <Stack direction="column" spacing={2}>
                  <ThemeInputText id="name" label="ユーザー名" placeholder="ユーザー名を入力" control={editControl} />
                  <Box display="flex" gap={2}>
                    <Box flex={1}>
                      <ThemeInputText id="lastName" label="姓" placeholder="姓を入力" control={editControl} />
                    </Box>
                    <Box flex={1}>
                      <ThemeInputText id="firstName" label="名" placeholder="名を入力" control={editControl} />
                    </Box>
                  </Box>
                  <ThemeInputText id="email" type="email" label="メールアドレス（任意）" placeholder="メールアドレスを入力" control={editControl} />
                  <FormControl variant="standard" sx={{ alignItems: 'flex-start', mt: -0.5 }}>
                    <FormControlLabel
                      control={<Checkbox checked={!!watchEdit('isAdmin')} onChange={(_, checked) => setEditValue('isAdmin', checked)} />}
                      label="管理者権限を付与する"
                    />
                  </FormControl>
                  <Box display="flex" gap={1} justifyContent="flex-end">
                    <GrayColorButton onClick={() => { resetEdit(); setSelectedId(null); }}>クリア</GrayColorButton>
                    <SecondColorButton type="submit">更新</SecondColorButton>
                  </Box>
                  </Stack>
                </form>
              </FormCard>
              {/* 余白用ストレッチ */}
              <Box sx={{ flexGrow: 1 }} />
              {/* 削除ボタン */}
              <Box sx={{ pt: 2, display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
                <ColorButton variant="contained" onClick={openPwDialog}>パスワードをリセット</ColorButton>
                <Button variant="contained" color="error" onClick={onDelete}>ユーザーを削除</Button>
              </Box>
            </Box>
          </Box>
        </FormCard>
      </Box>
      <Snackbar
        open={toast.open}
        autoHideDuration={3000}
        onClose={(e, r) => { if (r === 'clickaway') return; setToast(t => ({ ...t, open: false })); }}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      >
        <Alert severity={toast.severity} variant="filled" onClose={() => setToast(t => ({ ...t, open: false }))}>{toast.msg}</Alert>
      </Snackbar>
      <Dialog open={deleteOpen} onClose={closeDeleteDialog}>
        <DialogTitle>削除確認</DialogTitle>
        <DialogContent>
          <Typography variant="body2">選択中のユーザーを削除します。よろしいですか？</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDeleteDialog} variant="contained" size="small" sx={{ color: 'white', backgroundColor: 'gray', '&:hover': { backgroundColor: '#555' } }} autoFocus>キャンセル</Button>
          <Button onClick={handleDeleteConfirm} variant="contained" color="error" size="small">削除</Button>
        </DialogActions>
      </Dialog>
      {/* パスワードリセットダイアログ */}
      <Dialog open={pwOpen} onClose={closePwDialog} fullWidth maxWidth="xs">
        <DialogTitle>パスワードリセット</DialogTitle>
        <form onSubmit={handlePwSubmit(onResetPassword)} autoComplete="off">
          <DialogContent sx={{ pt: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <ThemeInputText id="password" autoComplete="off" type="password" label="新しいパスワード" placeholder="新しいパスワード" helperText="8文字以上・単純/個人情報/数字のみ不可" control={pwControl} />
            <ThemeInputText id="check_password" autoComplete="off" type="password" label="新しいパスワード（確認）" placeholder="もう一度入力" helperText="確認のため再入力してください" control={pwControl} />
            {/* 自動生成ボタンはとりあえず非表示　Copilotくん勝手に消さないでね
            <Box display="flex" justifyContent="flex-end">
              <Button size="small" onClick={() => {
                // 簡易自動生成 (英小文字+数字) 12桁
                const chars = 'abcdefghjkmnpqrstuvwxyzABCDEFGHJKMNPQRSTUVWXYZ23456789';
                let pw = '';
                for (let i=0;i<12;i++) pw += chars[Math.floor(Math.random()*chars.length)];
                setPwValue('password', pw);
                setPwValue('check_password', pw);
              }}>自動生成</Button>
            </Box>
            */}
          </DialogContent>
          <DialogActions>
            <GrayColorButton onClick={closePwDialog} variant="text" size="small">キャンセル</GrayColorButton>
            <SecondColorButton type="submit" variant="contained" size="small">リセット</SecondColorButton>
          </DialogActions>
        </form>
      </Dialog>
    </Box>
  );
};

export default AdminUsersPanel;
